import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import type { CategoryStore } from '@eliadmin/core/src/stores/category-store';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { infiniteQueryOptions } from '@tanstack/react-query';

type CategoryProductsFilters = Pick<
  CategoryStore['filter'],
  | 'manufacturers'
  | 'suppliers'
  | 'availabilities'
  | 'attributes'
  | 'sort'
  | 'price_min'
  | 'price_max'
>;

type CategoryProductsQueryOptions = {
  categoryId: number;
  locale: string;
  currentPage: string | null;
  authToken?: string;
  customerId?: number;
  filters: CategoryProductsFilters;
};

export const DEFAULT_CATEGORY_PRODUCTS_FILTERS: CategoryProductsFilters = {
  manufacturers: [],
  suppliers: [],
  availabilities: [],
  attributes: [],
  sort: {
    order: null,
    orderBy: null,
  },
  price_min: 0,
  price_max: 0,
};

export function createCategoryProductsInfiniteQuery({
  categoryId,
  locale,
  currentPage,
  authToken,
  customerId,
  filters,
}: CategoryProductsQueryOptions) {
  const queryWithoutPage = buildProductsQuery({
    attributes: filters.attributes,
    categories: [categoryId],
    manufacturers: filters.manufacturers,
    // Fills whole rows of the 2/3/5-column grid; 10 split Zahrada into 53 pages.
    results: 30,
    order: filters.sort.order,
    orderBy: filters.sort.orderBy,
    price_from: filters.price_min,
    price_to: filters.price_max,
    availabilities: filters.availabilities,
    suppliers: filters.suppliers,
  });

  const queryOptions = infiniteQueryOptions({
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    queryKey: [
      CacheKey.PRODUCTS,
      'infinite',
      currentPage,
      queryWithoutPage,
      customerId ?? null,
      authToken,
      locale,
      filters.attributes,
    ],
    queryFn: ({ pageParam, signal }) =>
      fetchProducts({
        query: {
          ...queryWithoutPage,
          page: pageParam,
        },
        locale,
        authToken,
        signal,
      }),
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page) {
        return lastPage.meta.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: currentPage ? parseInt(currentPage) : 1,
  });

  return { queryOptions, queryWithoutPage };
}
